import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  CircularProgress,
  Skeleton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PhoneDisabledIcon from "@mui/icons-material/PhoneDisabled";

const VideoConsultation = ({
  open,
  onClose,
  t,
  localVideoTrack,
  remoteVideoTrack,
  isPharmacistConnected,
  isConsultAcceptedCall,
  startRinging,
  connectedPharmacistName,
  isAudioOnlyConsult,
  onEndConsultation,
  isEndingConsultation = false,
}) => {
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);

  useEffect(() => {
    if (!localVideoTrack) {
      return;
    }

    const attachVideoTrack = () => {
      if (!localVideoRef.current) {
        return false;
      }

      if (!localVideoTrack.isEnabled) {
        try {
          localVideoTrack.enable();
        } catch (error) {
          return false;
        }
      }

      try {
        localVideoTrack.attach(localVideoRef.current);
      } catch (error) {
        return false;
      }
      
      if (localVideoRef.current) {
        const playPromise = localVideoRef.current.play();
        if (playPromise !== undefined) {
          playPromise.catch(() => {});
        }
      }
      
      return true;
    };

    if (localVideoRef.current) {
      attachVideoTrack();
    } else {
      let retryCount = 0;
      const maxRetries = 10;
      const retryInterval = 100;

      const retryTimer = setInterval(() => {
        retryCount++;
        
        if (localVideoRef.current) {
          clearInterval(retryTimer);
          attachVideoTrack();
        } else if (retryCount >= maxRetries) {
          clearInterval(retryTimer);
        }
      }, retryInterval);

      return () => {
        clearInterval(retryTimer);
        if (localVideoTrack && localVideoRef.current) {
          try {
            localVideoTrack.detach();
          } catch (error) {}
        }
      };
    }

    return () => {
      if (localVideoTrack && localVideoRef.current) {
        try {
          localVideoTrack.detach();
        } catch (error) {}
      }
    };
  }, [localVideoTrack]);

  useEffect(() => {
    if (remoteVideoTrack && remoteVideoRef.current) {
      remoteVideoTrack.attach(remoteVideoRef.current);
      
      if (remoteVideoRef.current) {
        remoteVideoRef.current.play().catch(() => {});
      }
      
      return () => {
        if (remoteVideoTrack) {
          remoteVideoTrack.detach();
        }
      };
    } else if (remoteVideoRef.current) {
      remoteVideoRef.current.srcObject = null;
    }
  }, [remoteVideoTrack]);

  const handleEndConsultation = () => {
    if (onEndConsultation) {
      onEndConsultation();
    }
  };

  const showLoading = !isPharmacistConnected || startRinging || (!localVideoTrack && !remoteVideoTrack && open);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
          backgroundColor: "#ffffff",
          maxHeight: "85vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 2,
          pt: 2.5,
          px: 3,
          borderBottom: "1px solid #e2e8f0",
          backgroundColor: "#f8fafc",
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, color: "#1e293b" }}>
          {t("Console.Consultation.VideoConsultation", {
            defaultValue: "Video Consultation",
          })}
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: "#64748b",
            "&:hover": {
              backgroundColor: "#e2e8f0",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent 
        sx={{ 
          p: 0, 
          backgroundColor: "#000000", 
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          position: "relative",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            width: "140px",
            height: "105px",
            borderRadius: 2,
            overflow: "hidden",
            border: localVideoTrack ? "2px solid #7c3aed" : "none",
            backgroundColor: "#000000",
            boxShadow: localVideoTrack 
              ? "0 4px 12px rgba(124, 58, 237, 0.4)" 
              : "none",
            zIndex: 10,
            display: localVideoTrack ? "block" : "none",
            transition: "all 0.2s ease",
          }}
        >
          <video
            ref={localVideoRef}
            autoPlay
            playsInline
            muted
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
          />
        </Box>

        {showLoading ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "400px",
              gap: 3,
              p: 4,
              backgroundColor: "#000000",
            }}
          >
            <Box sx={{ width: "100%", maxWidth: "800px", flex: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}>
              <Skeleton
                variant="rectangular"
                width="100%"
                height="100%"
                sx={{ borderRadius: 2, bgcolor: "#1e293b", minHeight: "400px" }}
                animation="wave"
              />
            </Box>
            <Box sx={{ textAlign: "center", mt: 2 }}>
              <Typography variant="h6" sx={{ color: "#ffffff", fontWeight: 600, mb: 1 }}>
                {t("Console.Consultation.Connecting", {
                  defaultValue: "Connecting...",
                })}
              </Typography>
              <Typography variant="body2" sx={{ color: "#94a3b8" }}>
                {t("Console.Consultation.WaitingForPharmacist", {
                  defaultValue: "Waiting for pharmacist to join",
                })}
              </Typography>
            </Box>
          </Box>
        ) : isAudioOnlyConsult ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "400px",
              gap: 3,
              p: 4,
              backgroundColor: "#000000",
            }}
          >
            <Box
              sx={{
                width: 120,
                height: 120,
                borderRadius: "50%",
                backgroundColor: "#1e293b",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography variant="h4" sx={{ color: "#7c3aed", fontWeight: 600 }}>
                {connectedPharmacistName ? connectedPharmacistName.charAt(0).toUpperCase() : "P"}
              </Typography>
            </Box>
            {connectedPharmacistName && (
              <Typography variant="h6" sx={{ color: "#ffffff", fontWeight: 600 }}>
                {connectedPharmacistName}
              </Typography>
            )}
            <Typography variant="body2" sx={{ color: "#94a3b8" }}>
              {t("Console.Consultation.AudioOnlyMode", {
                defaultValue: "Audio-only consultation",
              })}
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              position: "relative",
              width: "100%",
              height: "400px",
              backgroundColor: "#000000",
              overflow: "hidden",
            }}
          >
            {remoteVideoTrack ? (
              <video
                ref={remoteVideoRef}
                autoPlay
                playsInline
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
            ) : (
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 2,
                  color: "#94a3b8",
                }}
              >
                <Typography variant="h6">
                  {t("Console.Consultation.WaitingForVideo", {
                    defaultValue: "Waiting for video...",
                  })}
                </Typography>
              </Box>
            )}

            {isPharmacistConnected && connectedPharmacistName && (
              <Box
                sx={{
                  position: "absolute",
                  bottom: 12,
                  left: 12,
                  backgroundColor: "rgba(34, 197, 94, 0.9)",
                  color: "#ffffff",
                  px: 2,
                  py: 0.75,
                  borderRadius: 2,
                  zIndex: 10,
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {connectedPharmacistName}
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          p: 2.5,
          px: 3,
          backgroundColor: "#f8fafc",
          borderTop: "1px solid #e2e8f0",
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <Button
          variant="contained"
          color="error"
          onClick={handleEndConsultation}
          disabled={isEndingConsultation}
          startIcon={isEndingConsultation ? <CircularProgress size={16} color="inherit" /> : <PhoneDisabledIcon />}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            px: 3,
            py: 1,
            backgroundColor: "#ef4444",
            "&:hover": {
              backgroundColor: "#dc2626",
            },
            "&:disabled": {
              backgroundColor: "#cbd5e1",
              color: "#94a3b8",
            },
          }}
        >
          {isEndingConsultation
            ? t("Console.Consultation.Ending", { defaultValue: "Ending..." })
            : t("Console.Consultation.EndCall", { defaultValue: "End Call" })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

VideoConsultation.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  localVideoTrack: PropTypes.object,
  remoteVideoTrack: PropTypes.object,
  isPharmacistConnected: PropTypes.bool,
  isConsultAcceptedCall: PropTypes.bool,
  startRinging: PropTypes.bool,
  connectedPharmacistName: PropTypes.string,
  isAudioOnlyConsult: PropTypes.bool,
  onEndConsultation: PropTypes.func.isRequired,
  isEndingConsultation: PropTypes.bool,
};

VideoConsultation.defaultProps = {
  localVideoTrack: null,
  remoteVideoTrack: null,
  isPharmacistConnected: false,
  isConsultAcceptedCall: false,
  startRinging: false,
  connectedPharmacistName: "",
  isAudioOnlyConsult: false,
  isEndingConsultation: false,
};

export default VideoConsultation;



// WEBPACK FOOTER //
// ./src/components/assign-prescriptions/counter/components/VideoConsultation.jsx